import { useTranslation } from "react-i18next";

import RegPlate from "../../../../shared/RegPlate";
import { ICallQueueOverview, IUpNextRow, LeadAgeTone, UpNextFilter, CallLeadType } from "../../../../../models/CallQueue";
import { ISavCustomerName } from "../../../../../data/useSav";
import classNames from "../../../../../utils/classNames";
import { ageLabel, LEAD_TYPE_LABELS, Tone, vehicleTitle, websiteLabel } from "../../../../../utils/callQueue";
import PriceFormat from "../../../../../utils/priceFormat";
import Pill from "../Pill";

const AGE_TONES: Record<LeadAgeTone, Tone> = {
  [LeadAgeTone.Green]: "green",
  [LeadAgeTone.Amber]: "amber",
  [LeadAgeTone.Red]: "red",
};

function QueueRow({
  row,
  now,
  canOpen,
  onOpen,
  atfNames,
}: {
  row: IUpNextRow;
  now: Date;
  canOpen: boolean;
  onOpen: (leadId: string) => void;
  atfNames: ISavCustomerName[];
}) {
  const { t } = useTranslation("leads");
  const isNew = row.leadTypes.includes(CallLeadType.New);
  const typeTags = row.leadTypes.filter((type) => type !== CallLeadType.New);
  const atfName = row.atfCustomerId ? atfNames.find((x) => x.id === row.atfCustomerId)?.name : null;
  const callBackTime = row.callBackDueAt
    ? new Date(row.callBackDueAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  const content = (
    <>
      <span className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          <RegPlate vrm={row.vrm} />
          {row.isServedToMe && <span className="text-[11px] font-semibold text-primary-700">{t("ON CALL")}</span>}
          {callBackTime && <Pill tone="amber">{t("Call back")} {callBackTime}</Pill>}
          {typeTags.map((type) => <Pill key={type} tone="slate">{t(LEAD_TYPE_LABELS[type])}</Pill>)}
          {atfName && <Pill tone="slate">{atfName}</Pill>}
        </span>
        <Pill tone={AGE_TONES[row.ageTone]} className="flex-none">
          {ageLabel(row.created, now)}{isNew ? ` · ${t("new")}` : ""}
        </Pill>
      </span>
      <span className="text-[13px] font-semibold text-gray-900">{vehicleTitle(row.year, row.make, row.model) || t("Unknown vehicle")}</span>
      <span className="flex justify-between gap-2 text-xs text-gray-600">
        <span>
          {t("Quoted")} {PriceFormat(row.quotedPrice)}
          {row.attempts > 0 && ` · ${t("attempt {{n}}/{{max}}", { n: row.attempts + 1, max: row.maxAttempts })}`}
        </span>
        <span className="truncate text-right text-slate-600" title={t("Source")}>{websiteLabel(row.website, row.source)}</span>
      </span>
    </>
  );

  const className = classNames(
    "flex w-full flex-col gap-1.5 border-b border-gray-200 px-3.5 py-3 text-left",
    row.isServedToMe ? "bg-primary-50 ring-2 ring-inset ring-primary-600" : "bg-white"
  );

  // Agents are served leads in turn; approvers can jump to one, and so can anyone while the queue is unlocked.
  return canOpen ? (
    <button type="button" className={classNames(className, "min-h-[44px] hover:bg-gray-50")} onClick={() => onOpen(row.leadId)} aria-current={row.isServedToMe || undefined}>
      {content}
    </button>
  ) : (
    <div className={className} aria-current={row.isServedToMe || undefined}>{content}</div>
  );
}

export default function UpNextQueue({
  overview,
  filter,
  setFilter,
  onOpen,
  atfNames,
  isLoading,
}: {
  overview?: ICallQueueOverview;
  filter: UpNextFilter;
  setFilter: (filter: UpNextFilter) => void;
  onOpen: (leadId: string) => void;
  atfNames: ISavCustomerName[];
  isLoading: boolean;
}) {
  const { t } = useTranslation("leads");
  const now = new Date();

  const chips: { key: UpNextFilter; label: string }[] = [
    { key: UpNextFilter.AllDue, label: "All due" },
    { key: UpNextFilter.CallBacks, label: "Call backs" },
    { key: UpNextFilter.HighValue, label: "High value" },
    { key: UpNextFilter.Salvage, label: "Salvage" },
    ...(overview?.isSav ? [] : [{ key: UpNextFilter.Local, label: "Local" }]),
    { key: UpNextFilter.New, label: "New" },
  ];

  return (
    <aside aria-label={t("Call queue")} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-2.5 border-b border-gray-200 px-3.5 py-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-[15px] font-semibold text-gray-900">{t("Up next")}</h2>
          <span className="text-xs text-gray-600">{t("Sorted by priority")}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setFilter(chip.key)}
              aria-pressed={filter === chip.key}
              className={classNames(
                "rounded-full border px-2.5 py-1 text-xs font-medium",
                filter === chip.key ? "border-primary-600 bg-primary-600 text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              )}
            >
              {t(chip.label)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col">
        {isLoading && <p className="px-3.5 py-6 text-center text-sm text-gray-500">{t("Loading the queue…")}</p>}
        {!isLoading && (overview?.upNext.length ?? 0) === 0 && (
          <p className="px-3.5 py-6 text-center text-sm text-gray-500">{t("Nothing is due right now.")}</p>
        )}
        {overview?.upNext.map((row) => (
          <QueueRow key={row.leadId} row={row} now={now} canOpen={overview.canOpenLeads} onOpen={onOpen} atfNames={atfNames} />
        ))}
      </div>

      {overview && (
        <div className="flex flex-col gap-0.5 bg-gray-50 px-3.5 py-2.5 text-xs text-gray-600">
          <span>
            {t("{{count}} more waiting", { count: overview.moreWaiting })}
            {overview.oldestWaitingSince && ` · ${t("oldest")} ${ageLabel(overview.oldestWaitingSince, now)}`}
          </span>
          <span>{t("{{count}} with a manager", { count: overview.withManager })}</span>
        </div>
      )}
    </aside>
  );
}
